import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import SEOMeta from "../../components/base/SEOMeta";
import QuizStart from "./components/QuizStart";
import QuizQuestion from "./components/QuizQuestion";
import QuizResultScreen from "./components/QuizResult";
import { getAllQuizById } from "../../utils/allQuizzes";
import type { QuizResult } from "../../mocks/quizzes";

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

type Phase = "start" | "playing" | "result";

export default function QuizDetailPage() {
  const { id } = useParams<{ id: string }>();
  const quiz = getAllQuizById(id || "");

  const [phase, setPhase] = useState<Phase>("start");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [finalResult, setFinalResult] = useState<QuizResult | null>(null);

  if (!quiz) {
    return (
      <div className="min-h-screen flex flex-col" style={{ fontFamily: "'Nunito', sans-serif" }}>
        <SEOMeta
          title="Quiz Not Found | QuizVibe"
          description="This quiz could not be found. Browse hundreds of fun personality quizzes on QuizVibe!"
          canonicalPath="/categories"
        />
        <Navbar />
        <div className="flex-1 flex items-center justify-center flex-col gap-4 px-4">
          <div className="text-6xl">😅</div>
          <h2 className="text-2xl font-extrabold text-gray-800">Quiz not found!</h2>
          <p className="text-gray-500">This quiz might have moved or been removed.</p>
          <Link
            to="/categories"
            className="px-6 py-3 bg-[#FF6B6B] text-white font-bold rounded-full cursor-pointer whitespace-nowrap"
          >
            Browse Quizzes
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const quizUrl = `${SITE_URL}/quiz/${quiz.id}`;

  const quizJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": quiz.title,
      "description": quiz.description,
      "url": quizUrl,
      "inLanguage": "en",
      "image": quiz.image,
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
          { "@type": "ListItem", "position": 2, "name": "Quizzes", "item": `${SITE_URL}/categories` },
          { "@type": "ListItem", "position": 3, "name": quiz.title, "item": quizUrl }
        ]
      },
      "publisher": {
        "@type": "Organization",
        "name": "QuizVibe",
        "url": SITE_URL
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Quiz",
      "name": quiz.title,
      "description": quiz.description,
      "url": quizUrl,
      "image": quiz.image,
      "numberOfQuestions": quiz.questions.length,
      "educationalAlignment": {
        "@type": "AlignmentObject",
        "alignmentType": "educationalSubject",
        "targetName": quiz.categoryLabel
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": quiz.rating,
        "bestRating": 5,
        "ratingCount": quiz.totalPlays
      }
    }
  ];

  const handleStart = () => {
    setPhase("playing");
    setCurrentQuestionIndex(0);
    setAnswers({});
  };

  const handleAnswer = (value: string) => {
    const question = quiz.questions[currentQuestionIndex];
    const newAnswers = { ...answers, [question.id]: value };
    setAnswers(newAnswers);

    if (currentQuestionIndex + 1 < quiz.questions.length) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      const counts: Record<string, number> = {};
      Object.values(newAnswers).forEach((v) => {
        counts[v] = (counts[v] || 0) + 1;
      });
      const winnerKey = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
      const result = quiz.results.find((r) => r.key === winnerKey) || quiz.results[0];
      setFinalResult(result);
      setPhase("result");
    }
  };

  const handleRetake = () => {
    setPhase("start");
    setCurrentQuestionIndex(0);
    setAnswers({});
    setFinalResult(null);
  };

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Nunito', sans-serif" }}>
      <SEOMeta
        title={`${quiz.title} | QuizVibe`}
        description={quiz.description}
        keywords={`${quiz.tag} quiz, ${quiz.categoryLabel} quiz, personality quiz, kids quiz, ${quiz.title}`}
        canonicalPath={`/quiz/${quiz.id}`}
        ogImage={quiz.image}
        ogType="article"
        jsonLd={quizJsonLd}
      />
      <Navbar />

      {phase !== "playing" && (
        <div className="pt-16 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-6 py-3">
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <Link to="/" className="hover:text-[#FF6B6B] transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line" />
              <Link to="/categories" className="hover:text-[#FF6B6B] transition-colors cursor-pointer">Quizzes</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-gray-600 line-clamp-1">{quiz.title}</span>
            </div>
          </div>
        </div>
      )}

      {phase === "start" && (
        <QuizStart quiz={quiz} onStart={handleStart} />
      )}

      {phase === "playing" && (
        <div className="pt-16">
          <QuizQuestion
            question={quiz.questions[currentQuestionIndex]}
            questionIndex={currentQuestionIndex}
            totalQuestions={quiz.questions.length}
            onAnswer={handleAnswer}
          />
        </div>
      )}

      {phase === "result" && finalResult && (
        <QuizResultScreen quiz={quiz} result={finalResult} onRetake={handleRetake} />
      )}

      {phase !== "playing" && <Footer />}
    </div>
  );
}
